import styled, { css } from 'styled-components';
import { colors } from '@alixpartners/ui-components';

export const generateHighchartStyles = ({ svgElementClassName }) => {
	const svgElementClassNameSelector = `.${svgElementClassName}`;

	return css`
		${svgElementClassNameSelector} {
			overflow: visible;
			font-family: Roboto;
		}

		${svgElementClassNameSelector} .highcharts-tick {
			display: none;
		}

		${svgElementClassNameSelector} .highcharts-plot-band {
			fill-opacity: 0;
		}

		${svgElementClassNameSelector} .highcharts-plot-band:hover {
			fill-opacity: 0.05;
		}

		${svgElementClassNameSelector} .highcharts-halo {
			fill-opacity: 0;
			stroke-opacity: 0;
		}

		${svgElementClassNameSelector} .highcharts-axis-labels {
			color: ${colors.grey7} !important;
			font-size: 11px !important;
			font-family: Roboto;
		}

		${svgElementClassNameSelector} .highcharts-axis-labels tspan {
			color: ${colors.grey7} !important;
			font-size: 11px !important;
			font-family: Roboto;
		}

		${svgElementClassNameSelector} .highcharts-crosshair-category {
			stroke: ${colors.grey7};
			stroke-opacity: 1;
		}

		${svgElementClassNameSelector} .highcharts-yaxis-grid .highcharts-grid-line {
			stroke-opacity: 0;
		}

		${svgElementClassNameSelector} .highcharts-data-label {
			font-size: 11px;
		}

		.highcharts-legend-item .highcharts-point {
			width: 16px;
			height: 16px;
			rx: 0;
			x: 0;
			y: 2;
		}

		.highcharts-legend {
			pointer-events: none;
		}

		${svgElementClassNameSelector} .highcharts-background {
			fill: rgba(0, 0, 0, 0) !important;
		}

		${svgElementClassNameSelector} .highchart-custom-tooltip {
			padding: 16px 16px 0;
			margin: 0;
			background-color: ${colors.white};
			border: 0px;
			box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.25);
			position: relative;
		}

		${svgElementClassNameSelector} .highchart-custom-tooltip > li {
			margin-bottom: 8px;
			list-style: none;
		}

		.highchart-custom-tooltip > li:last-child {
			margin-bottom: 0;
		}

		${svgElementClassNameSelector} .highcharts-tooltip {
			fill-opacity: 0;
			background-color: transparent;
			stroke-opacity: 0;
		}

		${svgElementClassNameSelector} g.highcharts-tooltip {
			display: none;
		}

		${svgElementClassNameSelector} .highcharts-tooltip-box {
			fill: ${colors.white};
		}

		${svgElementClassNameSelector} .highcharts-column-series rect.highcharts-point {
			stroke: none;
		}
	`;
};

export const Container = styled.div`
	min-height: 550px;
`;

export const Wrapper = styled.div`
	z-index: 1;
	position: relative;

	display: flex;
	flex-direction: column;
	margin-top: 23px;
	margin-right: -10px;
	padding-left: 3px;

	${generateHighchartStyles};
`;

export const ChartWrapper = styled.div`
	margin-top: 20px;

	.highcharts-label {
		transition: opacity 0.5s ease-in;
	}

	.highcharts-container-loading .highcharts-label {
		opacity: 0 !important;
		transition: none;
	}
`;

export const ChartContainer = styled.div`
	width: ${props => props.width};
	position: relative;

	.highcharts-series {
		cursor: pointer;
	}

	.stacked-bar-xaxis-hidden {
		display: none;
	}

	.highcharts-series-inactive {
		opacity: 1;
	}

	.stacked-bar-tooltip--item {
		display: flex;
	}

	.stacked-bar-plotband {
		fill: ${colors.black};
		fill-opacity: 0;
		cursor: pointer;
		transform: scaleY(2) translateY(-5px);
	}

	.stacked-bar-plotband.stacked-bar-plotband__hover {
		stroke: ${colors.grey3};
		stroke-width: 1px;
	}

	.stacked-bar-plotband--selected {
		transform: scaleY(2) translateY(-5px);
	}

	.highcharts-container.bar-chart {
		overflow: auto;
	}

	.highcharts-column-series rect.highcharts-point {
		rx: 0;
	}
`;

export const LegendContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	margin-bottom: 24px;
	margin-right: 24px;
	visibility: ${props => (props.isHidden ? 'hidden' : 'visible')};
`;

export const LegendItem = styled.div`
	display: flex;
	align-items: center;
	margin-left: 24px;
`;

export const LegendColor = styled.div`
	width: 16px;
	height: 16px;
	border-radius: 1px;
	background-color: ${props => props.color};
`;

export const LegendName = styled.div`
	font-size: 11px;
	color: ${colors.grey5};
	margin-left: 8px;
	text-transform: uppercase;
`;

export const SelectedBackgroundContainer = styled.div`
	position: relative;
`;

export const SelectedBackground = styled.div`
	position: absolute;
	background: #444444; // #F7F7F7 to be the same as the background
	opacity: 0.05;
	z-index: 1;
`;

export const TooltipWrapper = styled.div`
	display: flex;
	flex-direction: column;
	padding: 5px;
	z-index: 1000;
`;

export const TooltipRowTitle = styled.div`
	display: flex;
	align-items: center;
	font-size: 13px;
	line-height: 19px;
	letter-spacing: 0.3px;
	color: ${colors.grey5};
	padding-right: 16px;
	max-width: 120px;
	width: 120px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	margin-right: 24px;
`;

export const TooltipRowValue = styled.div`
	display: flex;
	align-items: center;
	font-size: 13px;
	line-height: 19px;
	letter-spacing: 0.3px;
	color: ${colors.grey7};
	justify-content: flex-end;
	text-transform: uppercase;
`;

export const ColorWrapper = styled.div`
	display: flex;
	align-items: center;
`;

export const ColorSymbol = styled.div`
	min-width: 6px;
	max-width: 6px;
	min-height: 6px;
	max-height: 6px;
	background-color: ${({ color }) => color};
	border-radius: 50%;
`;

export const TooltipGrid = styled.div`
	display: grid;
	grid-template-columns: minmax(12px, 12px) 2fr 1fr;
	padding: 1px 2px;
	width: 100%;
`;

export const SummaryBackgroundWrapper = styled.div`
	position: absolute;
	height: ${props => props.height + 1 + 'px'};
	width: ${props => props.width + 'px'};
	background: ${colors.greyBg};
	border-bottom: 1px solid black;
	top: -20px;
	left: ${props => props.left + 3}px;
`;

export const SummaryTriangle = styled.div`
	position: absolute;
	width: 0px;
	height: 0px;
	border-top: ${props => props.size + 'px solid transparent'};
	border-bottom: ${props => props.size + 'px solid transparent'};
	border-left: ${props => `${props.size}px solid ${colors.greyBg}`};
	top: ${props => props.top + 'px'};
	right: ${props => -props.size + 'px'};
`;

export const HighlightBlock = styled.div`
	opacity: 0;
	cursor: pointer;
	box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.25);
	background-color: transparent;

	position: absolute;
	top: 0;
	bottom: 0;

	${({ width, left }) =>
		css`
			width: ${width}px;
			left: ${left}px;
		`};

	${({ isActive, isHighlight }) =>
		(isActive || isHighlight) &&
		css`
			opacity: 1;
		`};

	& > div:first-child {
		right: 0;
		top: 4px;
	}
`;

export const HighlightBlockWrapper = styled.div`
	position: absolute;
	top: -20px;
	background-color: transparent;

	${({ width, height, left }) =>
		css`
			width: ${width}px;
			height: ${height}px;
			left: ${left + 3}px;
		`};

	${({ isActive }) =>
		isActive &&
		css`
			${HighlightBlock}:hover {
				opacity: 1;
			}
		`};
`;

export const LoaderWrapper = styled.div`
	flex: 1;
	display: flex;
	align-items: center;
	height: 100%;
`;
